import { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';

export interface CartItem {
  id: string;
  type: 'coaching' | 'book';
  name: string;
  hours?: number;
  unitPrice: number;
  quantity: number;
  image?: string;
  packageKey?: string;
}

interface CartState {
  items: CartItem[];
  drawerOpen: boolean;
}

type CartAction =
  | { type: 'ADD_COACHING'; payload: { packageKey: string; name: string; hours: number } }
  | { type: 'ADD_BOOK'; payload: { id: string; name: string; unitPrice: number; image?: string } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_HOURS'; payload: { id: string; hours: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_DRAWER'; payload?: boolean }
  | { type: 'HYDRATE'; payload: CartItem[] };

const getPricePerHour = (totalHours: number) => {
  if (totalHours >= 7) return 57;
  if (totalHours >= 5) return 59;
  if (totalHours >= 3) return 65;
  return 70;
};

const getTotalCoachingHours = (items: CartItem[]) =>
  items.filter(i => i.type === 'coaching').reduce((sum, i) => sum + (i.hours || 0), 0);

const recalcCoachingPrices = (items: CartItem[]): CartItem[] => {
  const totalHours = getTotalCoachingHours(items);
  const pph = getPricePerHour(totalHours);
  return items.map(item =>
    item.type === 'coaching' ? { ...item, unitPrice: pph } : item
  );
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_COACHING': {
      const existing = state.items.find(i => i.type === 'coaching' && i.packageKey === action.payload.packageKey);
      let newItems: CartItem[];
      if (existing) {
        newItems = state.items.map(i =>
          i.id === existing.id ? { ...i, hours: (i.hours || 0) + action.payload.hours } : i
        );
      } else {
        newItems = [...state.items, {
          id: `coaching-${action.payload.packageKey}-${Date.now()}`,
          type: 'coaching',
          name: action.payload.name,
          hours: action.payload.hours,
          unitPrice: 70,
          quantity: 1,
          packageKey: action.payload.packageKey,
        }];
      }
      return { ...state, items: recalcCoachingPrices(newItems), drawerOpen: true };
    }
    case 'ADD_BOOK': {
      const existing = state.items.find(i => i.type === 'book' && i.id === action.payload.id);
      if (existing) return { ...state, drawerOpen: true };
      return {
        ...state,
        items: [...state.items, { ...action.payload, type: 'book', quantity: 1 }],
        drawerOpen: true,
      };
    }
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(i => i.id !== action.payload);
      return { ...state, items: recalcCoachingPrices(newItems) };
    }
    case 'UPDATE_HOURS': {
      const newItems = state.items.map(i =>
        i.id === action.payload.id ? { ...i, hours: Math.max(1, action.payload.hours) } : i
      );
      return { ...state, items: recalcCoachingPrices(newItems) };
    }
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        const newItems = state.items.filter(i => i.id !== action.payload.id);
        return { ...state, items: recalcCoachingPrices(newItems) };
      }
      const newItems = state.items.map(i =>
        i.id === action.payload.id ? { ...i, quantity: action.payload.quantity } : i
      );
      return { ...state, items: recalcCoachingPrices(newItems) };
    }
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'TOGGLE_DRAWER':
      return { ...state, drawerOpen: action.payload ?? !state.drawerOpen };
    case 'HYDRATE':
      return { ...state, items: recalcCoachingPrices(action.payload) };
    default:
      return state;
  }
}

interface CartContextValue {
  items: CartItem[];
  drawerOpen: boolean;
  addCoaching: (packageKey: string, name: string, hours: number) => void;
  addBook: (id: string, name: string, unitPrice: number, image?: string) => void;
  removeItem: (id: string) => void;
  updateHours: (id: string, hours: number) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleDrawer: (open?: boolean) => void;
  totalCoachingHours: number;
  pricePerHour: number;
  cartTotal: number;
  itemCount: number;
}

const CartContext = createContext<CartContextValue | null>(null);

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [], drawerOpen: false });

  // Hydrate from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('cart');
      if (saved) dispatch({ type: 'HYDRATE', payload: JSON.parse(saved) });
    } catch {}
  }, []);

  // Persist
  useEffect(() => {
    sessionStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  const totalCoachingHours = getTotalCoachingHours(state.items);
  const pricePerHour = getPricePerHour(totalCoachingHours);

  const cartTotal = state.items.reduce((sum, item) => {
    if (item.type === 'coaching') return sum + item.unitPrice * (item.hours || 0);
    return sum + item.unitPrice * item.quantity;
  }, 0);

  const itemCount = state.items.length;

  const value: CartContextValue = {
    items: state.items,
    drawerOpen: state.drawerOpen,
    addCoaching: useCallback((pk, name, hours) => dispatch({ type: 'ADD_COACHING', payload: { packageKey: pk, name, hours } }), []),
    addBook: useCallback((id, name, unitPrice, image) => dispatch({ type: 'ADD_BOOK', payload: { id, name, unitPrice, image } }), []),
    removeItem: useCallback((id) => dispatch({ type: 'REMOVE_ITEM', payload: id }), []),
    updateHours: useCallback((id, hours) => dispatch({ type: 'UPDATE_HOURS', payload: { id, hours } }), []),
    updateQuantity: useCallback((id, quantity) => dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } }), []),
    clearCart: useCallback(() => dispatch({ type: 'CLEAR_CART' }), []),
    toggleDrawer: useCallback((open?) => dispatch({ type: 'TOGGLE_DRAWER', payload: open }), []),
    totalCoachingHours,
    pricePerHour,
    cartTotal,
    itemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
